// ============================================================================
// CONSTANTS & CONFIG
// ============================================================================

const CONFIG = {
    MAX_ALTERNATIVES: 3,
    TOTAL_DURATION_MS: 60000,
    DRAWER_WIDTH: '420px',
    CACHE_DURATION_MS: 7 * 24 * 60 * 60 * 1000,
    COLORS: {
        success: '#10b981',
        error: '#ef4444'
    },
    LOADING_MESSAGES: [
        { limit: 0.15, text: "Wie Baumschulmeister geht das Lager checken..." },
        { limit: 0.30, text: "Gärtner studiert dein Foto mit Lupe..." },
        { limit: 0.45, text: "Pflanzen werden aus dem Katalog gewählt..." },
        { limit: 0.60, text: "Der Landschaftsarchitekt skizziert fleißig..." },
        { limit: 0.75, text: "Farbtöpfe werden gemischt..." },
        { limit: 0.90, text: "Letzte Blüten werden platziert..." },
        { limit: 0.95, text: "Gartenplan wird poliert..." }
    ],
    STARTER_PROMPTS: [
        "Ich möchte einen bienenfreundlichen, pflegeleichten Garten mit bunten Sträuchern.",
        "Ich wünsche mir einen modernen, cleanen Look mit Gräsern und einheitlichen Farben.",
        "Ein rustikaler Landhaus-Garten mit vielen blühenden Rosen und bunten Blüten.",
        "Pflegeleichter Garten mit immergrünen Hecken für Sichtschutz und wenig Rasen.",
        "Ich möchte einen naturnahen Garten mit heimischen Pflanzen."
    ],
    CHIP_PRESETS: [
        'mehr Blüten','mehr Gräser','mehr Immergrün','weiße Akzente','ruhiger',
        'natürlicher','moderner','weniger Pflege','mehr Sichtschutz'
    ]
};

const STORAGE_KEYS = {
    session: 'gartenplanerResults',
    addedCart: 'gartenplaner_added_alternatives'
};

// API Endpoint Configuration
function getApiUrl() {
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isDevelopment 
        ? 'http://localhost:5000/api/plan_garden'
        : 'https://gartenplanner-api.onrender.com/api/plan_garden';
}

// ============================================================================
// STATE
// ============================================================================

let alternativesCount = 0;
let _lastImageDataUri = null;
let pbTimer = null;
let pbValue = 0;
let lightbox = null;

// ============================================================================
// DOM ELEMENT CACHE
// ============================================================================

const DOM = {};

function cacheDOMElements() {
    DOM.inputSection = document.getElementById('inputSection');
    DOM.fileInput = document.getElementById('gardenImage');
    DOM.fileName = document.getElementById('fileName');
    DOM.deleteBtn = document.getElementById('deleteFileBtn');
    DOM.fileDrop = document.getElementById('fileDrop');
    DOM.submitBtn = document.getElementById('submitBtn');
    DOM.loadingDiv = document.getElementById('loadingDiv');
    DOM.errorBox = document.getElementById('errorBox');
    DOM.errorMessage = document.getElementById('errorMessage');
    DOM.results = document.getElementById('results');
    DOM.imageGrid = document.getElementById('imageGrid');
    DOM.plantAccordions = document.getElementById('plantAccordions');
    DOM.starterChips = document.getElementById('starterChips');
    DOM.description = document.getElementById('description');
    DOM.plantingMode = document.getElementById('plantingMode');
    DOM.pdfDownloadBtn = document.getElementById('pdfDownloadBtn');
    DOM.refreshBtn = document.getElementById('refreshBtn');
    DOM.createAdditionalBtn = document.getElementById('createAdditionalBtn');
    DOM.refineDrawer = document.getElementById('refineDrawer');
    DOM.closeRefineBtn = document.getElementById('closeRefineBtn');
    DOM.runRefineBtn = document.getElementById('runRefineBtn');
    DOM.refineText = document.getElementById('refineText');
    DOM.refineChips = document.getElementById('refineChips');
    DOM.refineLoadingDiv = document.getElementById('refineLoadingDiv');
    DOM.retryBtn = document.getElementById('retryBtn');
    DOM.progressBarInner = document.querySelector('#gp-planner .progress-bar-inner');
    DOM.loadingText = document.querySelector('#gp-planner .loading-text');
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function toast(message, bg) {
    const n = document.createElement('div');
    n.className = 'toast';
    n.style.background = bg || CONFIG.COLORS.success;
    n.textContent = message;
    document.getElementById('gp-planner').appendChild(n);
    setTimeout(() => { 
        n.style.animation = 'slideOut .2s ease'; 
        setTimeout(() => n.remove(), 200); 
    }, 2500);
}

function markAlternativeAsAdded(altIndex) {
    const added = JSON.parse(localStorage.getItem(STORAGE_KEYS.addedCart) || '[]');
    if (!added.includes(altIndex)) {
        added.push(altIndex);
        localStorage.setItem(STORAGE_KEYS.addedCart, JSON.stringify(added));
    }
}

function isAlternativeAdded(altIndex) {
    const added = JSON.parse(localStorage.getItem(STORAGE_KEYS.addedCart) || '[]');
    return added.includes(altIndex);
}

// ============================================================================
// IMAGE PROCESSING
// ============================================================================

function isImageLikeString(s) {
    if (typeof s !== 'string') return false;
    if (/^data:image\//i.test(s)) return true;
    if (/^https?:\/\//i.test(s) && /\.(png|jpe?g|webp)(\?|#|$)/i.test(s)) return true;
    return false;
}

function pickFromObj(o) {
    if (!o || typeof o !== 'object') return null;
    return o.data_uri || o.dataURL || o.dataUrl || o.url || o.src || o.image || null;
}

function deepCollectImages(node, acc, limit = 12) {
    if (acc.length >= limit || !node) return;
    
    if (Array.isArray(node)) {
        node.forEach(n => deepCollectImages(n, acc, limit));
        return;
    }
    
    if (typeof node === 'string') {
        if (isImageLikeString(node)) acc.push(node);
        return;
    }
    
    if (typeof node === 'object') {
        const direct = pickFromObj(node);
        if (direct && isImageLikeString(String(direct))) acc.push(String(direct));
        
        for (const k in node) {
            if (!Object.prototype.hasOwnProperty.call(node, k)) continue;
            deepCollectImages(node[k], acc, limit);
            if (acc.length >= limit) break;
        }
    }
}

function normalizeImages(raw) {
    let candidates = raw?.image_data_uris || raw?.images || raw?.image_urls || raw?.generated_images || raw?.outputs || [];
    let list = [];
    
    if (Array.isArray(candidates) && candidates.length) {
        list = candidates.map(it => {
            if (typeof it === 'string') return it;
            const p = pickFromObj(it);
            return p || null;
        }).filter(Boolean);
    }
    
    if (list.length === 0) {
        const acc = [];
        deepCollectImages(raw, acc, 12);
        list = acc;
    }
    
    const seen = new Set();
    const out = [];
    list.forEach(s => {
        if (!isImageLikeString(String(s))) return;
        const key = String(s);
        if (!seen.has(key)) {
            seen.add(key);
            out.push(String(s));
        }
    });
    
    return out;
}

// ============================================================================
// SIMPLE IMAGE ZOOM (Shopify-Style)
// ============================================================================

function initImageZoom() {
    document.querySelectorAll('.pswp-link').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const imgSrc = link.getAttribute('href');
            const imgAlt = link.querySelector('img')?.alt || 'Gartenplan';
            openZoomOverlay(imgSrc, imgAlt);
        });
    });
}

function openZoomOverlay(src, alt) {
    const overlay = document.createElement('div');
    overlay.className = 'zoom-overlay';
    overlay.innerHTML = `
        <div class="zoom-overlay-bg"></div>
        <button class="zoom-close" aria-label="Schließen">×</button>
        <div class="zoom-container">
            <img src="${src}" alt="${alt}" class="zoom-image">
        </div>
    `;
    
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    
    const img = overlay.querySelector('.zoom-image');
    const container = overlay.querySelector('.zoom-container');
    const closeBtn = overlay.querySelector('.zoom-close');
    const bg = overlay.querySelector('.zoom-overlay-bg');
    
    let scale = 1;
    let panning = false;
    let pointX = 0;
    let pointY = 0;
    let start = { x: 0, y: 0 };
    
    function setTransform() {
        img.style.transform = `translate(${pointX}px, ${pointY}px) scale(${scale})`;
    }
    
    // Double-click zoom
    img.addEventListener('dblclick', (e) => {
        if (scale === 1) {
            scale = 2.5;
            const rect = img.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            pointX = (rect.width / 2 - x) * 1.5;
            pointY = (rect.height / 2 - y) * 1.5;
        } else {
            scale = 1;
            pointX = 0;
            pointY = 0;
        }
        setTransform();
    });
    
    // Mouse wheel zoom
    container.addEventListener('wheel', (e) => {
        e.preventDefault();
        const xs = (e.clientX - pointX) / scale;
        const ys = (e.clientY - pointY) / scale;
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        scale = Math.min(Math.max(1, scale + delta), 4);
        pointX = e.clientX - xs * scale;
        pointY = e.clientY - ys * scale;
        if (scale === 1) {
            pointX = 0;
            pointY = 0;
        }
        setTransform();
    }, { passive: false });
    
    // Pan
    img.addEventListener('mousedown', (e) => {
        if (scale > 1) {
            e.preventDefault();
            start = { x: e.clientX - pointX, y: e.clientY - pointY };
            panning = true;
            img.style.cursor = 'grabbing';
        }
    });
    
    window.addEventListener('mousemove', (e) => {
        if (!panning) return;
        e.preventDefault();
        pointX = e.clientX - start.x;
        pointY = e.clientY - start.y;
        setTransform();
    });
    
    window.addEventListener('mouseup', () => {
        panning = false;
        img.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    });
    
    // Touch support
    let initialDistance = 0;
    let initialScale = 1;
    
    container.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            initialDistance = Math.hypot(
                e.touches[0].pageX - e.touches[1].pageX,
                e.touches[0].pageY - e.touches[1].pageY
            );
            initialScale = scale;
        } else if (e.touches.length === 1 && scale > 1) {
            start = { 
                x: e.touches[0].clientX - pointX, 
                y: e.touches[0].clientY - pointY 
            };
            panning = true;
        }
    }, { passive: false });
    
    container.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const distance = Math.hypot(
                e.touches[0].pageX - e.touches[1].pageX,
                e.touches[0].pageY - e.touches[1].pageY
            );
            scale = Math.min(Math.max(1, initialScale * (distance / initialDistance)), 4);
            setTransform();
        } else if (panning && e.touches.length === 1) {
            e.preventDefault();
            pointX = e.touches[0].clientX - start.x;
            pointY = e.touches[0].clientY - start.y;
            setTransform();
        }
    }, { passive: false });
    
    container.addEventListener('touchend', () => {
        panning = false;
        if (scale === 1) {
            pointX = 0;
            pointY = 0;
            setTransform();
        }
    });
    
    // Close
    const closeOverlay = () => {
        overlay.remove();
        document.body.style.overflow = '';
    };
    
    closeBtn.addEventListener('click', closeOverlay);
    bg.addEventListener('click', closeOverlay);
    
    document.addEventListener('keydown', function escClose(e) {
        if (e.key === 'Escape') {
            closeOverlay();
            document.removeEventListener('keydown', escClose);
        }
    });
    
    // Animate in
    setTimeout(() => overlay.classList.add('active'), 10);
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

function setProgress(p) {
    if (!DOM.progressBarInner) return;
    pbValue = Math.max(pbValue, Math.min(p, 1));
    DOM.progressBarInner.style.width = Math.round(pbValue * 100) + '%';

    if (DOM.loadingText) {
        const currentMessage = CONFIG.LOADING_MESSAGES.find(m => pbValue <= m.limit) || 
                             CONFIG.LOADING_MESSAGES[CONFIG.LOADING_MESSAGES.length - 1];
        DOM.loadingText.textContent = currentMessage.text;
    }
}

function bumpProgress(delta, cap = 0.95) {
    const next = Math.min(pbValue + delta, cap);
    setProgress(next);
}

function startProgress(duration = CONFIG.TOTAL_DURATION_MS) {
    pbValue = 0;
    setProgress(0.02);
    const startTime = Date.now();

    if (pbTimer) clearInterval(pbTimer);

    pbTimer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progressRatio = elapsed / duration;
        
        let targetProgress;
        if (progressRatio < 0.5) {
            targetProgress = progressRatio * 1.4;
        } else {
            const secondHalfProgress = (progressRatio - 0.5) * 2;
            targetProgress = 0.7 + (secondHalfProgress * 0.25);
        }
        
        targetProgress = Math.min(0.95, targetProgress);
        
        if (pbValue < targetProgress) {
            const step = (targetProgress - pbValue) * 0.15;
            setProgress(pbValue + Math.max(step, 0.005));
        }
    }, 150);
}

function reach(progress) {
    if (pbTimer) {
        clearInterval(pbTimer);
        pbTimer = null;
    }
    setProgress(progress);
}

function doneProgress() {
    if (pbTimer) {
        clearInterval(pbTimer);
        pbTimer = null;
    }
    setProgress(1);
}

function resetProgress() {
    if (pbTimer) {
        clearInterval(pbTimer);
        pbTimer = null;
    }
    pbValue = 0;
    setProgress(0);
    if (DOM.loadingText) {
        DOM.loadingText.textContent = "Der Gartenplan wird erstellt.";
    }
}

// ============================================================================
// TABLE GENERATION
// ============================================================================

function generatePlantTable(alternative) {
    if (!alternative || alternative.length === 0) {
        return '<p>Keine Pflanzen verfügbar</p>';
    }
    
    let html = `<div class="table-scroll-wrapper"><table><thead><tr><th>Pflanze</th><th>Anzahl</th><th>Einzelpreis</th><th>Gesamt</th></tr></thead><tbody>`;
    
    alternative.forEach(row => {
        const isSumRow = row.Pflanze === 'SUMME';
        const displayName = row.display_name || row.Pflanze || '-';
        const hasLink = row.product_url && !isSumRow;
        
        const plantNameCell = hasLink 
            ? `<a href="${row.product_url}" target="_blank" rel="noopener noreferrer" class="plant-link" title="Zum Produkt">${displayName}</a>`
            : displayName;
        
        html += `
            <tr class="${isSumRow ? 'sum-row' : ''}">
                <td>${isSumRow ? 'SUMME' : plantNameCell}</td>
                <td>${row.Anzahl || (isSumRow ? '' : '-')}</td>
                <td>${row.Einzelpreis || (isSumRow ? '' : '-')}</td>
                <td>${row.Gesamt || '-'}</td>
            </tr>`;
    });
    
    html += `</tbody></table></div>`;
    return html;
}

// ============================================================================
// RENDER FUNCTIONS
// ============================================================================

function renderImageCard(imgDataUri, title, altIdx) {
    const isAdded = isAlternativeAdded(altIdx);
    const btnClass = isAdded ? 'btn-primary btn-add-to-cart added-to-cart' : 'btn-primary btn-add-to-cart';
    const btnText = isAdded ? 'Im Warenkorb' : 'In den Warenkorb';
    const btnDisabled = isAdded ? 'disabled' : '';
    const btnDataAdded = isAdded ? 'data-added="true"' : '';

    const src = String(imgDataUri);
    const card = document.createElement('div');
    card.className = 'image-card';
    card.setAttribute('data-alt-index', altIdx);

    card.innerHTML = `
        <a href="${src}" class="pswp-link" data-pswp-width="1920" data-pswp-height="1080">
            <img class="thumb" loading="lazy" src="${src}" alt="${title}">
        </a>
        <h4>${title}</h4>
        <div style="display:flex; gap:10px; justify-content:center; margin-top:8px;">
            <button class="${btnClass}" ${btnDisabled} ${btnDataAdded} onclick="addAlternativeToCart(${altIdx})">${btnText}</button>
        </div>
    `;
    
    return card;
}

function renderPlantAccordion(alternative, title, description, altIdx) {
    const isAdded = isAlternativeAdded(altIdx);
    const btnClass = isAdded ? 'btn-primary btn-add-to-cart added-to-cart' : 'btn-primary btn-add-to-cart';
    const btnText = isAdded ? 'Im Warenkorb' : 'In den Warenkorb';
    const btnDisabled = isAdded ? 'disabled' : '';
    const btnDataAdded = isAdded ? 'data-added="true"' : '';

    const wrapper = document.createElement('details');
    wrapper.setAttribute('data-alt-index', altIdx);
    
    const summary = document.createElement('summary');
    summary.innerHTML = `<span>${title}</span><span class="caret">▶</span>`;
    
    const body = document.createElement('div');
    body.className = 'accordion-body';

    let descriptionHtml = '';
    if (description && description.trim()) {
        // Prüfe ob bereits HTML (enthält Tags)
        const isHTML = /<[a-z][\s\S]*>/i.test(description);
        
        if (isHTML) {
            // Backend hat bereits HTML geliefert
            descriptionHtml = `<div class="design-description">${description}</div>`;
        } else {
            // Markdown zu HTML konvertieren
            let html = description
                .replace(/### (.+)/g, '<h4>$1</h4>') // ### → <h4>
                .split('\n\n')
                .filter(p => p.trim())
                .map(p => p.startsWith('<h4>') ? p : `<p>${p.trim()}</p>`)
                .join('');
            descriptionHtml = `<div class="design-description">${html}</div>`;
        }
    }

    body.innerHTML = `
        ${generatePlantTable(alternative)}
        ${descriptionHtml}
        <button class="${btnClass}" ${btnDisabled} ${btnDataAdded} onclick="addAlternativeToCart(${altIdx})">${btnText}</button>
    `;
    
    wrapper.appendChild(summary);
    wrapper.appendChild(body);
    
    return wrapper;
}

function saveToStorage(data, images, alternatives, titles, descriptions) {
    try {
        const storageData = {
            data: data,
            images: images,
            alternatives: alternatives,
            titles: titles,
            descriptions: descriptions,
            timestamp: Date.now(),
            expiresAt: Date.now() + CONFIG.CACHE_DURATION_MS
        };
        localStorage.setItem(STORAGE_KEYS.session, JSON.stringify(storageData));
        if (images[0]) _lastImageDataUri = images[0];
    } catch (e) {
        console.error('Storage error:', e);
    }
}

function displayResults(data) {
    DOM.imageGrid.innerHTML = '';
    DOM.plantAccordions.innerHTML = '';

    const images = normalizeImages(data);
    const alternatives = data.plant_alternatives || [];
    const titles = data.alternative_titles || [];
    const descriptions = data.alternative_descriptions || [];

    DOM.inputSection.classList.add('hidden');
    
    if (DOM.refineText && !DOM.refineText.value) {
        const currentDesc = DOM.description?.value || '';
        DOM.refineText.value = currentDesc.trim();
    }

    if (images.length === 0) {
        console.warn('Keine Bilder vom Backend erkannt.');
    }

    if (images.length > 0) {
        const imgDataUri = images[0];
        const apiTitle = (titles[0] && String(titles[0]).trim()) || null;
        const title = apiTitle || 'Dein Gartenplan';
        const altIdx = 1;

        const card = renderImageCard(imgDataUri, title, altIdx);
        DOM.imageGrid.appendChild(card);
        alternativesCount = 1;
    }

    if (alternatives.length > 0) {
        const alternative = alternatives[0];
        const altIdx = 1;
        const apiTitle = (titles[0] && String(titles[0]).trim()) || null;
        const title = apiTitle || 'Gartenplan';
        const description = descriptions[0] || '';

        const accordion = renderPlantAccordion(alternative, title, description, altIdx);
        DOM.plantAccordions.appendChild(accordion);
    }

    window.plantAlternatives = alternatives;
    DOM.results.classList.add('show');
    DOM.results.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (DOM.createAdditionalBtn && alternativesCount < CONFIG.MAX_ALTERNATIVES) {
        DOM.createAdditionalBtn.style.display = 'block';
    } else if (DOM.createAdditionalBtn) {
        DOM.createAdditionalBtn.style.display = 'none';
    }

    saveToStorage(data, images, alternatives, titles, descriptions);
    
    // PhotoSwipe initialisieren
    setTimeout(() => initImageZoom(), 100);   // ✅ Neue Funktion
}

function appendVariant(data) {
    const currentCount = DOM.imageGrid.querySelectorAll('.image-card').length;
    const idx = currentCount + 1;
    const imgs = normalizeImages(data);
    const img = imgs[0];

    const apiTitle = (data.alternative_titles && data.alternative_titles[0])
        ? String(data.alternative_titles[0]).trim()
        : null;
    const title = apiTitle || `Alternative ${idx}`;

    if (img) {
        const card = renderImageCard(img, title, idx);
        DOM.imageGrid.appendChild(card);
        _lastImageDataUri = String(img);
        alternativesCount = idx;
    }

    const alt = (data.plant_alternatives || [])[0] || null;
    const description = (data.alternative_descriptions || [])[0] || '';

    if (alt) {
        const accordion = renderPlantAccordion(alt, title, description, idx);
        DOM.plantAccordions.appendChild(accordion);

        window.plantAlternatives = (window.plantAlternatives || []);
        window.plantAlternatives[idx - 1] = alt;
    }

    try {
        const saved = localStorage.getItem(STORAGE_KEYS.session);
        let storageData;
        
        if (saved) {
            storageData = JSON.parse(saved);
            if (!storageData.images) storageData.images = [];
            if (!storageData.alternatives) storageData.alternatives = [];
            if (!storageData.titles) storageData.titles = [];
            if (!storageData.descriptions) storageData.descriptions = [];
            
            if (img) storageData.images.push(img);
            if (alt) storageData.alternatives.push(alt);
            if (title) storageData.titles.push(title);
            if (description) storageData.descriptions.push(description);
        } else {
            storageData = {
                images: img ? [img] : [],
                alternatives: alt ? [alt] : [],
                titles: title ? [title] : [],
                descriptions: description ? [description] : [],
                timestamp: Date.now(),
                expiresAt: Date.now() + CONFIG.CACHE_DURATION_MS
            };
        }
        
        localStorage.setItem(STORAGE_KEYS.session, JSON.stringify(storageData));
    } catch (e) {
        console.error('Storage error:', e);
    }

    const totalCount = document.querySelectorAll('#imageGrid .image-card').length;
    if (totalCount >= CONFIG.MAX_ALTERNATIVES) {
        if (DOM.createAdditionalBtn) DOM.createAdditionalBtn.style.display = 'none';
        toast(`Maximal ${CONFIG.MAX_ALTERNATIVES} Alternativen erreicht`, '#2FAE55');
    } else {
        if (DOM.createAdditionalBtn) DOM.createAdditionalBtn.style.display = 'block';
    }
    
    // PhotoSwipe neu initialisieren
    setTimeout(() => initImageZoom(), 100);   // ✅ Neue Funktion
}

// ============================================================================
// CART FUNCTIONALITY
// ============================================================================

async function updateCartCount() {
    try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const els = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
        els.forEach(el => { el.textContent = cart.item_count; });
    } catch (e) {
        console.error('Failed to update cart count:', e);
    }
}

async function addAlternativeToCart(alternativeIndex) {
    if (!window.plantAlternatives || !window.plantAlternatives[alternativeIndex - 1]) {
        console.error('Alternative nicht gefunden');
        return;
    }
    
    const alternative = window.plantAlternatives[alternativeIndex - 1];
    
    const allButtons = document.querySelectorAll(`[onclick*="addAlternativeToCart(${alternativeIndex})"]`);
    
    allButtons.forEach(btn => {
        btn.disabled = true;
        btn.textContent = 'Wird hinzugefügt...';
    });

    try {
        const items = alternative
            .filter(row => row.Pflanze !== 'SUMME' && row.variant_id)
            .map(row => ({ id: row.variant_id, quantity: row.Anzahl }));

        if (window.location.protocol === 'file:') {
            return new Promise(resolve => {
                setTimeout(() => {
                    markAlternativeAsAdded(alternativeIndex);
                    updateCartButtons(alternativeIndex, true);
                    toast(`LOKALER TEST: Warenkorb-Simulation erfolgreich.`, CONFIG.COLORS.success);
                    resolve();
                }, 500);
            });
        }
        
        if (items.length === 0) {
            throw new Error('Keine gültigen Produkte mit Variant ID gefunden');
        }
        
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Fehler beim Hinzufügen zum Warenkorb');
        }
        
        await response.json();

        markAlternativeAsAdded(alternativeIndex);
        updateCartButtons(alternativeIndex, true);
        updateCartCount();
        
        toast('Zum Warenkorb hinzugefügt!', CONFIG.COLORS.success);
    } catch (error) {
        console.error('Cart error:', error);
        updateCartButtons(alternativeIndex, false);
        toast('Fehler: ' + error.message, CONFIG.COLORS.error);
    }
}

function updateCartButtons(alternativeIndex, isAdded) {
    const allButtons = document.querySelectorAll(`[onclick*="addAlternativeToCart(${alternativeIndex})"]`);
    
    allButtons.forEach(btn => {
        if (isAdded) {
            btn.textContent = 'Im Warenkorb';
            btn.classList.add('added-to-cart');
            btn.setAttribute('data-added', 'true');
            btn.disabled = true;
        } else {
            btn.textContent = 'In den Warenkorb';
            btn.classList.remove('added-to-cart');
            btn.removeAttribute('data-added');
            btn.disabled = false;
        }
    });
}

window.addAlternativeToCart = addAlternativeToCart;

// ============================================================================
// PDF GENERATION
// ============================================================================

async function generatePDF() {
    if (!window.plantAlternatives || window.plantAlternatives.length === 0) {
        toast('Bitte zuerst einen Gartenplan erstellen.', CONFIG.COLORS.error);
        return;
    }

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pdfWidth - 2 * margin;
    const logoHeight = 10;

    const cardElements = [...document.querySelectorAll('#imageGrid .image-card')];
    const plantSections = [...document.querySelectorAll('#plantAccordions details')];
    
    toast(`Starte PDF-Erstellung für ${cardElements.length} Alternative(n)...`, '#2FAE55');
    
    const pdfContainer = document.createElement('div');
    pdfContainer.id = 'pdf-render-temp';
    pdfContainer.style.width = `${contentWidth}mm`;
    pdfContainer.style.margin = '0 auto';
    pdfContainer.style.padding = '0';
    pdfContainer.style.backgroundColor = 'white';
    pdfContainer.style.position = 'fixed';
    pdfContainer.style.left = '-9999px';
    pdfContainer.style.top = '0';
    document.body.appendChild(pdfContainer);

    const tempStyles = document.createElement('style');
    tempStyles.textContent = `
        #pdf-render-temp h3 { font-size: 14px; margin-bottom: 8px; }
        #pdf-render-temp h4 { font-size: 12px; margin: 5px 0 8px; }
        #pdf-render-temp .thumb { 
            width: 100%; 
            height: auto; 
            max-height: 100%; 
            border-radius: 8px;
            object-fit: contain;
            display: block;
        }
        #pdf-render-temp table { width: 100% !important; min-width: 0 !important; table-layout: auto; border: 1px solid #000; }
        #pdf-render-temp thead th { background: #15873F; color: #fff; padding: 4px; font-size: 9px; }
        #pdf-render-temp td { padding: 3px; font-size: 9px; border-bottom: 1px solid #ddd; }
        #pdf-render-temp .image-card { padding: 0 !important; }
        #pdf-render-temp .file-name-container, #pdf-render-temp .btn-add-to-cart { display: none !important; }
        #pdf-render-temp .design-description { font-size: 10px; padding: 10px; margin: 10px 0; line-height: 1.4; }
        #pdf-render-temp .design-description p { margin-bottom: 8px; }
        #pdf-render-temp .plant-link { 
            text-decoration: none !important; 
            border: none !important; 
            color: inherit !important; 
            pointer-events: none !important; 
        }
        #pdf-render-temp .plant-link::after { 
            content: "" !important; 
            display: none !important; 
        }
    `;
    document.head.appendChild(tempStyles);

    plantSections.forEach(d => d.setAttribute('open', ''));

    try {
        for (let i = 0; i < cardElements.length; i++) {
            const card = cardElements[i];
            const altIndex = card.getAttribute('data-alt-index') || (i + 1);
            const plantDetail = plantSections.find(d => d.getAttribute('data-alt-index') == altIndex);
            
            if (i > 0) pdf.addPage();
            let currentY = margin;
            
            try {
                await new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'Anonymous';
                    img.onload = () => {
                        try {
                            pdf.addImage(img, 'PNG', margin, currentY, (img.width / img.height) * logoHeight, logoHeight);
                            pdf.setFontSize(14);
                            pdf.text('Pflanzenplaner', margin + 15, currentY + 4);
                            pdf.setFontSize(9);
                            pdf.text(`Alternative ${altIndex}`, margin + 15, currentY + 8);
                            currentY += logoHeight + 5;
                        } catch (e) {
                            console.warn('Logo konnte nicht hinzugefügt werden:', e);
                            pdf.setFontSize(14);
                            pdf.text('Pflanzenplaner', margin, currentY + 5);
                            pdf.setFontSize(9);
                            pdf.text(`Alternative ${altIndex}`, margin, currentY + 10);
                            currentY += logoHeight + 5;
                        }
                        resolve();
                    };
                    img.onerror = () => {
                        pdf.setFontSize(14);
                        pdf.text('Pflanzenplaner', margin, currentY + 5);
                        pdf.setFontSize(9);
                        pdf.text(`Alternative ${altIndex}`, margin, currentY + 10);
                        currentY += logoHeight + 5;
                        resolve();
                    };
                    img.src = 'https://baumfink.de/cdn/shop/files/Baumfink_Logo_14_171x221.png?v=1685520878';
                });
            } catch (e) {
                console.warn('Logo-Fehler:', e);
            }
            
            const originalImg = card.querySelector('.thumb');
            if (originalImg) {
                const imgSrc = originalImg.src;
                
                await new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'Anonymous';
                    img.onload = () => {
                        try {
                            const imgNaturalWidth = img.naturalWidth || img.width;
                            const imgNaturalHeight = img.naturalHeight || img.height;
                            const aspectRatio = imgNaturalHeight / imgNaturalWidth;
                            const maxImageWidth = contentWidth;
                            const maxImageHeight = 120;
                            
                            let imgWidth = maxImageWidth;
                            let imgHeight = imgWidth * aspectRatio;
                            
                            if (imgHeight > maxImageHeight) {
                                imgHeight = maxImageHeight;
                                imgWidth = imgHeight / aspectRatio;
                            }
                            
                            const imgX = margin + (contentWidth - imgWidth) / 2;
                            pdf.addImage(img, 'JPEG', imgX, currentY, imgWidth, imgHeight);
                            currentY += imgHeight + 5;
                        } catch (e) {
                            console.error('Fehler beim Hinzufügen des Gartenbildes:', e);
                            currentY += 5;
                        }
                        resolve();
                    };
                    img.onerror = () => {
                        console.error('Fehler beim Laden des Bildes');
                        currentY += 5;
                        resolve();
                    };
                    img.src = imgSrc;
                });
            }

            if (plantDetail) {
                pdfContainer.innerHTML = '';
                const listSection = document.createElement('div');
                const plantTitle = plantDetail.querySelector('summary span')?.textContent || `Alternative ${altIndex}`;
                const bodyContent = plantDetail.querySelector('.accordion-body');
                
                if (bodyContent) {
                    listSection.innerHTML = `<h4>Pflanzenliste: ${plantTitle}</h4>` + bodyContent.innerHTML;
                    
                    const cartBtn = listSection.querySelector('.btn-add-to-cart');
                    if (cartBtn) cartBtn.remove();
                    
                    const tableWrapper = listSection.querySelector('.table-scroll-wrapper');
                    if (tableWrapper) {
                        tableWrapper.style.overflowX = 'visible';
                        tableWrapper.style.boxShadow = 'none';
                        tableWrapper.style.borderRadius = '0';
                    }
                    
                    pdfContainer.appendChild(listSection);
                    pdfContainer.style.display = 'block';

                    try {
                        const canvasTable = await html2canvas(pdfContainer, {
                            scale: 2,
                            useCORS: true,
                            allowTaint: true,
                            logging: false,
                            scrollY: 0,
                            windowWidth: pdfContainer.offsetWidth
                        });
                        
                        pdfContainer.style.display = 'none';

                        const tableData = canvasTable.toDataURL('image/jpeg', 0.85);
                        const tableProps = pdf.getImageProperties(tableData);
                        const tableHeight = contentWidth * (tableProps.height / tableProps.width);

                        if (currentY + tableHeight > pdfHeight - margin) {
                            pdf.addPage();
                            currentY = margin;
                        }

                        pdf.addImage(tableData, 'JPEG', margin, currentY, contentWidth, tableHeight);
                    } catch (e) {
                        console.error('Fehler beim Rendern der Tabelle:', e);
                    }
                }
            }
        }

        pdf.save('Gartenplan_Ergebnisse.pdf');
        toast('PDF erfolgreich heruntergeladen!', CONFIG.COLORS.success);

    } catch (error) {
        console.error('Fehler bei der PDF-Generierung:', error);
        toast('Fehler beim PDF-Download: ' + error.message, CONFIG.COLORS.error);
    } finally {
        if (pdfContainer) pdfContainer.remove();
        if (tempStyles && tempStyles.parentNode) document.head.removeChild(tempStyles);
        plantSections.forEach(d => d.removeAttribute('open'));
    }
}

// ============================================================================
// STORAGE & RESTORE
// ============================================================================

function restoreResults() {
    const saved = localStorage.getItem(STORAGE_KEYS.session);
    if (saved) {
        try {
            const storageData = JSON.parse(saved);
            
            if (storageData.expiresAt && Date.now() > storageData.expiresAt) {
                localStorage.removeItem(STORAGE_KEYS.session);
                return;
            }
            
            const images = storageData.images || [];
            const alternatives = storageData.alternatives || [];
            const titles = storageData.titles || [];
            const descriptions = storageData.descriptions || [];
            
            if (images.length === 0) {
                console.warn('Keine gespeicherten Bilder gefunden');
                return;
            }
            
            const data = {
                status: 'success',
                image_data_uris: images,
                plant_alternatives: alternatives,
                alternative_titles: titles,
                alternative_descriptions: descriptions
            };
            
            if (DOM.results && !DOM.results.classList.contains('show')) {
                displayResults(data);
                console.log('Gartenplaner: Ergebnisse erfolgreich wiederhergestellt');
            }
        } catch (e) {
            console.error('Failed to restore results:', e);
            localStorage.removeItem(STORAGE_KEYS.session);
        }
    }
}

// ============================================================================
// UI ACTIONS
// ============================================================================

function resetPlanner() {
    if (confirm('Möchtest du wirklich einen neuen Entwurf starten? Deine aktuellen Ergebnisse werden gelöscht.')) {
        localStorage.removeItem(STORAGE_KEYS.session);
        localStorage.removeItem(STORAGE_KEYS.addedCart);
        
        if (DOM.results) DOM.results.classList.remove('show');
        if (DOM.inputSection) DOM.inputSection.classList.remove('hidden');
        
        const form = document.getElementById('plannerForm');
        if (form) form.reset();
        
        if (DOM.fileInput) DOM.fileInput.value = '';
        if (DOM.fileName) DOM.fileName.textContent = '';
        if (DOM.deleteBtn) DOM.deleteBtn.style.display = 'none';
        if (DOM.imageGrid) DOM.imageGrid.innerHTML = '';
        if (DOM.plantAccordions) DOM.plantAccordions.innerHTML = '';
        
        alternativesCount = 0;
        
        if (DOM.createAdditionalBtn) DOM.createAdditionalBtn.style.display = 'none';
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast('Bereit für einen neuen Entwurf!', CONFIG.COLORS.success);
    }
}

function openRefine() {
    const currentDesc = DOM.description?.value.trim() || '';
    if (currentDesc) {
        DOM.refineText.value = currentDesc;
    }
    
    if (DOM.refineDrawer.style.right === '0px') {
        DOM.refineDrawer.style.right = `-${CONFIG.DRAWER_WIDTH}`;
    } else {
        DOM.refineDrawer.style.right = '0';
    }
}

function closeRefine() {
    DOM.refineDrawer.style.right = `-${CONFIG.DRAWER_WIDTH}`;
}

function buildRefineDescription() {
    const base = DOM.description?.value.trim() || '';
    const extra = (DOM.refineText?.value || '').trim();
    return extra ? `${base}\n${extra}` : base;
}

function updateRefineTextFromChips() {
    const chips = [...document.querySelectorAll('#refineChips .active')].map(el => el.textContent);
    let currentText = (DOM.refineText.value || '').split('\n').filter(line => !line.startsWith('Anpassungen:')).join('\n').trim();
    
    if (chips.length > 0) {
        const chipsLine = 'Anpassungen: ' + chips.join(', ');
        currentText = currentText ? `${currentText}\n${chipsLine}` : chipsLine;
    }
    DOM.refineText.value = currentText.trim();
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
    cacheDOMElements();

    // File handling
    if (DOM.fileInput && DOM.fileName && DOM.deleteBtn && DOM.fileDrop) {
        DOM.fileInput.addEventListener('change', (e) => {
            if (e.target.files.length > 0) {
                DOM.fileName.textContent = e.target.files[0].name;
                DOM.deleteBtn.style.display = 'inline-block';
            } else {
                DOM.fileName.textContent = 'Keine Datei ausgewählt';
                DOM.deleteBtn.style.display = 'none';
            }
        });

        DOM.deleteBtn.addEventListener('click', (e) => {
            e.preventDefault();
            DOM.fileInput.value = '';
            _lastImageDataUri = null;
            DOM.fileName.textContent = 'Keine Datei ausgewählt';
            DOM.deleteBtn.style.display = 'none';
            sessionStorage.removeItem(STORAGE_KEYS.session);
            if (DOM.inputSection) DOM.inputSection.classList.remove('hidden');
            if (DOM.results) DOM.results.classList.remove('show');
        });

        ['dragenter', 'dragover'].forEach(evt => {
            DOM.fileDrop.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                DOM.fileDrop.classList.add('dragover');
            });
        });

        ['dragleave', 'drop'].forEach(evt => {
            DOM.fileDrop.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                DOM.fileDrop.classList.remove('dragover');
            });
        });

        DOM.fileDrop.addEventListener('drop', (e) => {
            const dt = e.dataTransfer;
            if (!dt || !dt.files || !dt.files.length) return;
            const file = dt.files[0];
            if (!/image\/(png|jpe?g|webp)/i.test(file.type)) {
                toast('Bitte PNG, JPG oder WEBP hochladen', CONFIG.COLORS.error);
                return;
            }
            const d = new DataTransfer();
            d.items.add(file);
            DOM.fileInput.files = d.files;
            DOM.fileInput.dispatchEvent(new Event('change'));
        });
    }

    // Paste handling
    document.addEventListener('paste', (e) => {
        if (!e.clipboardData) return;
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.kind === 'file') {
                const file = it.getAsFile();
                if (file && /image\/(png|jpe?g|webp)/i.test(file.type)) {
                    const d = new DataTransfer();
                    d.items.add(file);
                    DOM.fileInput.files = d.files;
                    DOM.fileInput.dispatchEvent(new Event('change'));
                    toast('Bild eingefügt', CONFIG.COLORS.success);
                    break;
                }
            }
        }
    });

    // Starter chips
    if (DOM.starterChips) {
        CONFIG.STARTER_PROMPTS.forEach(prompt => {
            const chip = document.createElement('span');
            chip.textContent = prompt;
            chip.addEventListener('click', () => {
                DOM.description.value = prompt;
                toast('Prompt übernommen', CONFIG.COLORS.success);
            });
            DOM.starterChips.appendChild(chip);
        });
    }

    // Planting mode sync
    const plantingModeRadios = document.querySelectorAll('input[name="modeSelector"]');
    if (plantingModeRadios.length > 0 && DOM.plantingMode) {
        plantingModeRadios.forEach(radio => {
            radio.addEventListener('change', () => {
                if (radio.checked) {
                    DOM.plantingMode.value = radio.value;
                }
            });
        });
        
        const initialChecked = document.querySelector('input[name="modeSelector"]:checked');
        if (initialChecked) {
            DOM.plantingMode.value = initialChecked.value;
        }
    }

    // Submit button
    DOM.submitBtn.addEventListener('click', async function() {
        const description = DOM.description.value.trim();
        const plantingModeRadio = document.querySelector('input[name="modeSelector"]:checked');
        const plantingMode = plantingModeRadio ? plantingModeRadio.value : 'border_lawn';
        const imageFile = DOM.fileInput.files[0];
        const apiUrl = getApiUrl();

        if (!description) {
            alert('Bitte beschreibe deinen Garten.');
            return;
        }
        if (!imageFile) {
            alert('Bitte lade ein Gartenfoto hoch.');
            return;
        }

        const reader = new FileReader();
        reader.onloadstart = () => reach(0.10);
        reader.onprogress = () => bumpProgress(0.03, 0.35);
        reader.onload = () => reach(0.35);

        reader.onload = async function(event) {
            const base64DataUri = event.target.result;
            _lastImageDataUri = base64DataUri;

            DOM.loadingDiv.classList.add('show');
            DOM.results.classList.remove('show');
            DOM.errorBox.classList.remove('show');
            DOM.submitBtn.disabled = true;
            startProgress();

            try {
                reach(0.40);
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ description, image_data_uri: base64DataUri, planting_mode: plantingMode })
                });

                if (!response.ok) {
                    let errorData;
                    try {
                        errorData = await response.json();
                    } catch (e) {
                        errorData = { error: `Serverfehler (${response.status} ${response.statusText})` };
                    }
                    throw new Error(errorData.error || `Server Error: ${response.status} ${response.statusText}`);
                }

                reach(0.65);
                const data = await response.json();
                if (data.status !== 'success') {
                    throw new Error(data.error || 'Unbekannter Fehler');
                }

                reach(0.85);
                displayResults(data);
                reach(0.95);
                doneProgress();
                toast('Fertig.', CONFIG.COLORS.success);
            } catch (error) {
                console.error('Error:', error);
                DOM.errorMessage.textContent = error.message;
                DOM.errorBox.classList.add('show');
                doneProgress();
                toast('Fehler bei der Generierung.', CONFIG.COLORS.error);
            } finally {
                DOM.loadingDiv.classList.remove('show');
                DOM.submitBtn.disabled = false;
                resetProgress();
            }
        };
        
        reader.onerror = function() {
            alert('Fehler beim Lesen der Bilddatei.');
            DOM.loadingDiv.classList.remove('show');
            DOM.submitBtn.disabled = false;
        };
        
        reader.readAsDataURL(imageFile);
    });

    // PDF download
    if (DOM.pdfDownloadBtn) {
        DOM.pdfDownloadBtn.addEventListener('click', generatePDF);
    }

    // Refresh button
    if (DOM.refreshBtn) {
        DOM.refreshBtn.addEventListener('click', resetPlanner);
    }

    // Retry button
    if (DOM.retryBtn) {
        DOM.retryBtn.addEventListener('click', () => {
            DOM.errorBox.classList.remove('show');
            DOM.submitBtn.click();
        });
    }

    // Refine drawer
    if (DOM.createAdditionalBtn) {
        DOM.createAdditionalBtn.addEventListener('click', openRefine);
    }
    if (DOM.closeRefineBtn) {
        DOM.closeRefineBtn.addEventListener('click', closeRefine);
    }

    // Refine chips
    if (DOM.refineChips) {
        CONFIG.CHIP_PRESETS.forEach(txt => {
            const c = document.createElement('span');
            c.className = 'chip';
            c.textContent = txt;
            c.style.cssText = 'display:inline-block;padding:8px 12px;border:1px solid var(--baumfink-border-light);border-radius:999px;cursor:pointer;font-size:14px;background:#fff;user-select:none;';
            c.addEventListener('click', () => {
                c.classList.toggle('active');
                c.style.borderColor = c.classList.contains('active') ? 'var(--baumfink-green-button)' : 'var(--baumfink-border-light)';
                c.style.background = c.classList.contains('active') ? '#F1FBF4' : '#fff';
                updateRefineTextFromChips();
            });
            DOM.refineChips.appendChild(c);
        });
    }

    // Run refine
    if (DOM.runRefineBtn) {
        DOM.runRefineBtn.addEventListener('click', async () => {
            const currentCount = document.querySelectorAll('#imageGrid .image-card').length;
            if (currentCount >= CONFIG.MAX_ALTERNATIVES) {
                toast(`Maximal ${CONFIG.MAX_ALTERNATIVES} Alternativen möglich`, CONFIG.COLORS.error);
                closeRefine();
                return;
            }

            const apiUrl = getApiUrl();
            const plantingModeRadio = document.querySelector('input[name="modeSelector"]:checked');
            const plantingMode = plantingModeRadio ? plantingModeRadio.value : 'border_lawn';

            let imageDataUri = _lastImageDataUri;
            if (!imageDataUri) {
                const file = DOM.fileInput.files[0];
                if (!file) {
                    toast('Kein Bild vorhanden. Bitte zuerst hochladen.', CONFIG.COLORS.error);
                    return;
                }
                imageDataUri = await new Promise((resolve, reject) => {
                    const r = new FileReader();
                    r.onload = e => resolve(e.target.result);
                    r.onerror = () => reject(new Error('Bild konnte nicht gelesen werden.'));
                    r.readAsDataURL(file);
                });
            }

            const description = buildRefineDescription();

            DOM.refineLoadingDiv.classList.add('show');
            DOM.runRefineBtn.disabled = true;
            DOM.loadingDiv.classList.add('show');
            startProgress();

            try {
                reach(0.20);
                const bodyPayload = {
                    description,
                    image_data_uri: imageDataUri,
                    planting_mode: plantingMode
                };

                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(bodyPayload)
                });
                
                if (!response.ok) {
                    let err;
                    try {
                        err = await response.json();
                    } catch (e) {
                        err = { error: `HTTP ${response.status}` };
                    }
                    throw new Error(err.error || 'Fehler beim Erzeugen der Variante');
                }
                
                reach(0.65);
                const data = await response.json();
                if (data.status !== 'success') {
                    throw new Error(data.error || 'Unbekannter Fehler');
                }

                appendVariant(data);
                reach(0.95);
                doneProgress();
                toast('Neue Variante hinzugefügt.', CONFIG.COLORS.success);
                closeRefine();
                document.querySelectorAll('#refineChips .active').forEach(c => c.classList.remove('active'));
                DOM.refineText.value = '';
            } catch (e) {
                doneProgress();
                toast('Fehler: ' + e.message, CONFIG.COLORS.error);
            } finally {
                DOM.loadingDiv.classList.remove('show');
                DOM.refineLoadingDiv.classList.remove('show');
                resetProgress();
                DOM.runRefineBtn.disabled = false;
            }
        });
    }

    // Restore results on load
    restoreResults();

    // Restore on back/forward navigation
    window.addEventListener('pageshow', function(event) {
        if (event.persisted || (window.performance && window.performance.navigation.type === 2)) {
            console.log('Gartenplaner: Seite aus Cache geladen');
            restoreResults();
        }
    });

    // Fallback restore
    setTimeout(function() {
        const saved = localStorage.getItem(STORAGE_KEYS.session);
        if (saved && DOM.results && !DOM.results.classList.contains('show')) {
            console.log('Gartenplaner: Fallback-Wiederherstellung');
            restoreResults();
        }
    }, 100);
});